// The singer's line on Sing in Solfège's staff (issue #3722): the take's pitch samples
// (solfegeTraceSamples) turned into smooth, coloured SVG paths, so at the end of a take the singer
// sees where they were high and where they were low. Pure - no React, no DOM - so the live staff,
// the result and the stories draw the same line from the same samples, and the tests can hold it
// to what "professional" means here: smooth, unbroken while the voice sings, coloured in clean runs.
//
// What arrives is one sample about every 30 ms of voice (solfegeTraceSampleMs), each a fractional
// midi number already folded into the singer's register when octaves count (solfegeFoldCents, as the
// session folds every reading), its cents from the note it was read against, and the time the singer
// sang it on phraseNoteSpans' scale. In order:
//
//   1. Clean. Samples that are not numbers are dropped, and so is every sample from the moment the
//      last note ends: the take runs a little past it so the recording and the last grading window
//      close, but the line is the phrase's and stops with it, whatever a caller hands in. The rest
//      are ordered by time, and one closer than minSpacingMs to the one kept before it is dropped.
//   2. Fragments. The line breaks where the voice stopped for more than breakGapMs, and a fragment
//      shorter than minFragmentMs (a cough, a detector blip) is not drawn. Nor is one with no
//      confident reading in it: every reading weak, too unclear to count toward a grade, is a breath
//      or a sound in the room rather than singing. A weak reading inside a fragment that has confident
//      ones is part of the singing and draws with it.
//   3. One octave. Folding toward each sample's own note can still make the folded pitch jump by an
//      octave: where the singer's register changes (a man an octave down for a phrase, then in the
//      note's own octave for a note), and wherever a detector's octave error got through. The voice
//      is continuous, so within a fragment the samples are drawn in the octaves that make the line
//      most continuous, a sample kept where the session folded it unless moving it removes such a
//      jump. A change of target or register therefore never makes a spike: the line is the voice's
//      pitch, and only its colour depends on the target.
//   4. Smooth. A five-sample median removes detector outliers without rounding off a real change
//      of note, then a light exponential average, run forward and back so it draws nothing late,
//      takes out jitter and most vibrato, which at a few pixels per beat would otherwise draw as a
//      zigzag. Only the last few points of a live line move as samples arrive.
//   5. Colour. Each point is coloured by its distance from the note sounding when it was sung (by
//      time, not by the note the estimator was aimed at), folded into the singer's register by the
//      session's own rule (solfegeFoldCents, the register carried along the line) when octaves
//      count, in the bands a note is graded by (solfegeCentsColor). The distance is the median's, not the
//      average's, which rounds a change of note off on both sides and would colour a clean
//      arrival as close. The settle at the start of each note, which grading skips while the
//      voice travels to the note (solfegeSettleMs, the one formula grading uses), is not judged
//      either: it takes the colour of what follows it.
//      Then no colour run is shorter than minColorRunMs: the shortest run is merged into its
//      neighbour until none is, so a voice hovering at a band's edge draws one colour, not a
//      flicker. A fragment shorter than strayFragmentMs that is nowhere within closeCents of the note
//      sounding - red from end to end - is a stray mark, not a note, and is not drawn: on a phone,
//      after the singer's line had stopped in the last held note of Abide With Me, a short red dash
//      was drawn up and to the right, away from everything sung (issue #3722). A note sung that short
//      and that wrong is rare, no graded note is shorter than minGradedMs (300 ms), and Easy is
//      forgiving, so nothing sung is lost. A dropped fragment does not move the register either.
//   6. Join the dot. During the take the live dot runs ahead of the line by the voice's lag and the
//      frame delay; when the dot is voiced its position is passed as `lead` and becomes the line's
//      last point, so there is no gap between them. A dot past the last note's end is not joined.
//   7. Draw. Points become x by time and y by pitch through the staff's geometry, and the curve
//      through them is a monotone cubic in x (Fritsch-Carlson tangents, as d3's curveMonotoneX),
//      written as cubic Béziers. It is Catmull-Rom's smoothness without its two faults on this data:
//      it never loops back in x where neighbouring slots run at different speeds, and never
//      overshoots a note it arrives at. The tangents are computed over the whole fragment and each
//      colour run takes its own segments, ending on the point the next run starts from, so runs
//      meet with no gap and no kink.
//
// No NaN or Infinity ever reaches a path: non-numbers are dropped at the door, x and y are clamped
// by the geometry, and points with no width between them are merged before any slope is taken.
//
// Cost. During the take the samples change about every 30 ms but the dot every frame and tick, so
// the work is in two parts: solfegeTraceLine does steps 1 to 5 and draws every fragment but the last,
// keyed on the samples, and solfegeTraceJoin joins the dot to the last fragment and draws that one,
// on every render. solfegeTracePaths is the two in one, and draws exactly what they draw.

import { LearnLevel } from '../../../../../common/src/model.ts';
import { ResultColor } from '../../../../../common/src/rhythm_echo.ts';
import {
  solfegeCentsColor,
  solfegeFoldCents,
  SolfegeLevelParams,
  solfegeLevelParams,
  solfegeSettleMs,
} from '../../../../../common/src/sing_in_solfege.ts';
import { SolfegeTraceSample, solfegeTraceSampleMs } from './solfege_session.ts';
import { solfegePitchY, SolfegeStaffGeometry, solfegeTimeX } from './solfege_staff_geometry.ts';

// The line's tunables, in one place
export const solfegeTraceTuning = {
  // The spacing the session keeps samples at; a sample closer than half of it to the one before is
  // a duplicate and dropped
  sampleSpacingMs: solfegeTraceSampleMs,
  minSpacingMs: solfegeTraceSampleMs / 2,
  // The median's width in samples (150 ms): wide enough to drop two outliers in a row, narrow
  // enough to keep the start of a quick note
  medianWidth: 5,
  // The exponential average's weight on each new (median) sample, run forward and back for the
  // drawn line only: 0.4 brings a steady voice's jitter down to under 0.4 of the detector's and
  // flattens a 5.5 Hz vibrato to a ripple, with no lag (the colour is judged without it)
  smoothing: 0.4,
  // A silence longer than this breaks the line (five missing samples: a breath, not a dropout)
  breakGapMs: 150,
  // A fragment shorter than this is not drawn
  minFragmentMs: 80,
  // A fragment shorter than this that is never within closeCents of the note sounding is a stray
  // mark (a sound in the room, a misreading), not a note, and is not drawn
  strayFragmentMs: 200,
  // What drawing a sample an octave from where the session folded it costs, in semitones of
  // jump, when the line is made continuous (see oneOctave)
  octavePenalty: 0.5,
  // No colour run is shorter than this, so the line does not flicker at a band's edge
  minColorRunMs: 100,
  // The dot is joined to the line only when the line's last sample is at most this far behind it:
  // the voice's lag and the frame delay (70 to 135 ms) and a sample's spacing, with room to spare
  leadJoinMs: 300,
  // The stroke, in the 350-wide viewBox: about 3.5 px on a phone, with round caps and joins
  strokeWidth: 3.5,
};

const tuning = solfegeTraceTuning;

// Where the dot is when it is voiced: the line's last point is joined to it
export interface SolfegeTraceLead {
  intoMs: number;
  midi: number;
}

// The live dot as the line's lead: only a voiced dot is joined to the line
export function solfegeTraceLead(
  dot: { intoMs: number; voiced: boolean; midi?: number } | undefined,
): SolfegeTraceLead | undefined {
  if (!dot?.voiced || dot.midi === undefined) {
    return undefined;
  }
  return { intoMs: dot.intoMs, midi: dot.midi };
}

export interface SolfegeTracePoint {
  intoMs: number;
  midi: number; // smoothed
  x: number;
  y: number;
  color: ResultColor;
}

// One colour run of one fragment of the line: its SVG path and the points it passes through, the
// last of which (unless the run ends its fragment) is the first point of the next run
export interface SolfegeTracePath {
  color: ResultColor;
  d: string;
  fragment: number;
  points: SolfegeTracePoint[];
}

interface Raw {
  intoMs: number;
  midi: number;
}

interface Sampled extends Raw {
  weak: boolean; // the reading was too unclear to count toward a grade
}

interface Smoothed extends Raw {
  judgedMidi: number; // what its colour is judged on
  judgedCents: number; // its distance from the note sounding then, folded into the singer's register
}

// The line up to the dot: the smoothed fragments, every fragment but the last already drawn, and the
// register the line ends in, for the dot's own distance. Opaque to the screens: they pass it on.
export interface SolfegeTraceLine {
  readonly lines: readonly Smoothed[][];
  readonly drawn: readonly SolfegeTracePath[];
  readonly register: number;
  readonly params: SolfegeLevelParams;
}

export function solfegeTracePaths(
  samples: readonly SolfegeTraceSample[],
  geometry: SolfegeStaffGeometry,
  { params = solfegeLevelParams[LearnLevel.Easy], lead }: { params?: SolfegeLevelParams; lead?: SolfegeTraceLead } = {},
): SolfegeTracePath[] {
  return solfegeTraceJoin(solfegeTraceLine(samples, geometry, { params }), geometry, lead);
}

// Steps 1 to 5, and every fragment but the last drawn: the part that changes only with the samples
export function solfegeTraceLine(
  samples: readonly SolfegeTraceSample[],
  geometry: SolfegeStaffGeometry,
  { params = solfegeLevelParams[LearnLevel.Easy] }: { params?: SolfegeLevelParams } = {},
): SolfegeTraceLine {
  if (geometry.notes.length === 0) {
    return { lines: [], drawn: [], register: 0, params };
  }
  const fragments = splitFragments(clean(samples, geometry.lengthMs)).filter(
    (fragment) => durationOf(fragment) >= tuning.minFragmentMs && fragment.some((sample) => !sample.weak),
  );
  const smoothed = fragments.map((fragment) => smooth(params.octaveCounts ? oneOctave(fragment) : fragment));
  // The register runs along the whole line, from one fragment into the next, as the session's does;
  // a stray fragment, which is not drawn, leaves it where it was
  let register = 0;
  const lines: Smoothed[][] = [];
  for (const line of smoothed) {
    let carried = register;
    const folded = line.map((point) => {
      const fold = solfegeFoldCents(centsFromSounding(geometry, point.intoMs, point.judgedMidi), carried, params);
      carried = fold.register;
      return { ...point, judgedCents: fold.cents };
    });
    if (isStray(folded, params)) {
      continue;
    }
    register = carried;
    lines.push(folded);
  }
  const drawn = lines.slice(0, -1).flatMap((line, fragment) => draw(line, geometry, params, fragment));
  return { lines, drawn, register, params };
}

// Step 6, and the last fragment drawn: the dot joined to the line, on every render
export function solfegeTraceJoin(
  line: SolfegeTraceLine,
  geometry: SolfegeStaffGeometry,
  lead?: SolfegeTraceLead,
): SolfegeTracePath[] {
  const { lines, drawn, register, params } = line;
  const last = lines[lines.length - 1];
  if (!last) {
    return [...drawn];
  }
  const joined = joinLead(last, lead, geometry, register, params);
  return [...drawn, ...draw(joined, geometry, params, lines.length - 1)];
}

function durationOf(fragment: readonly Raw[]): number {
  return fragment[fragment.length - 1].intoMs - fragment[0].intoMs;
}

// A short fragment that is nowhere close to the note sounding: a stray mark, not a note (step 5)
function isStray(line: Smoothed[], params: SolfegeLevelParams): boolean {
  return (
    durationOf(line) < tuning.strayFragmentMs && line.every((point) => Math.abs(point.judgedCents) > params.closeCents)
  );
}

// A pitch's distance in cents from the note sounding at `intoMs`, unfolded
function centsFromSounding(geometry: SolfegeStaffGeometry, intoMs: number, midi: number): number {
  return (midi - soundingNote(geometry, intoMs).midi) * 100;
}

// ---- 1. Clean --------------------------------------------------------------------------------

function clean(samples: readonly SolfegeTraceSample[], lengthMs: number): Sampled[] {
  const numbers = samples
    .filter((sample) => Number.isFinite(sample.intoMs) && Number.isFinite(sample.midi) && sample.intoMs < lengthMs)
    .map(({ intoMs, midi, weak }) => ({ intoMs, midi, weak }))
    .sort((first, second) => first.intoMs - second.intoMs);
  const kept: Sampled[] = [];
  for (const sample of numbers) {
    const last = kept[kept.length - 1];
    if (!last || sample.intoMs - last.intoMs >= tuning.minSpacingMs) {
      kept.push(sample);
    }
  }
  return kept;
}

// ---- 2. Fragments ----------------------------------------------------------------------------

function splitFragments(samples: Sampled[]): Sampled[][] {
  const fragments: Sampled[][] = [];
  for (const sample of samples) {
    const current = fragments[fragments.length - 1];
    const last = current?.[current.length - 1];
    if (!last || sample.intoMs - last.intoMs > tuning.breakGapMs) {
      fragments.push([sample]);
    } else {
      current.push(sample);
    }
  }
  return fragments;
}

// ---- 3. One octave ---------------------------------------------------------------------------

// `midi` moved by whole octaves to be as near `to` as it can
function nearestOctave(midi: number, to: number): number {
  return midi - 12 * Math.round((midi - to) / 12);
}

// The octave each sample of a fragment is drawn in: the one that makes the line most continuous,
// each sample moved off the octave the session folded it to costing a little (octavePenalty), so a
// stretch is moved only to remove a jump of about an octave, and a real leap of a sixth stays a
// leap. The cheapest choice over the whole fragment, by dynamic programming over the three
// octaves each sample could be in; on a tie a sample stays where it was folded.
const octaveShifts = [0, -12, 12];

function oneOctave(fragment: Raw[]): Raw[] {
  let costs = octaveShifts.map((shift) => (shift === 0 ? 0 : tuning.octavePenalty));
  const choices: number[][] = [];
  for (let index = 1; index < fragment.length; index += 1) {
    const midi = fragment[index].midi;
    const before = fragment[index - 1].midi;
    const picked: number[] = [];
    costs = octaveShifts.map((shift) => {
      let best = 0;
      let bestCost = Infinity;
      octaveShifts.forEach((shiftBefore, choice) => {
        const cost = costs[choice] + Math.abs(midi + shift - (before + shiftBefore));
        if (cost < bestCost) {
          best = choice;
          bestCost = cost;
        }
      });
      picked.push(best);
      return bestCost + (shift === 0 ? 0 : tuning.octavePenalty);
    });
    choices.push(picked);
  }
  let choice = costs.indexOf(Math.min(...costs));
  const shifts = new Array<number>(fragment.length);
  for (let index = fragment.length - 1; index >= 0; index -= 1) {
    shifts[index] = octaveShifts[choice];
    choice = index > 0 ? choices[index - 1][choice] : choice;
  }
  return fragment.map((sample, index) => ({ ...sample, midi: sample.midi + shifts[index] }));
}

// The note sounding at a moment of the phrase; the first before it, the last after it
function soundingNote(geometry: SolfegeStaffGeometry, intoMs: number) {
  const notes = geometry.notes;
  return notes.find((note) => intoMs < note.slot.endMs) ?? notes[notes.length - 1];
}

// ---- 4. Smooth -------------------------------------------------------------------------------

function median(values: number[]): number {
  const sorted = [...values].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// The drawn pitch: the median, then the average, run forward and then back so that it has no lag
// (a one-way average would draw every arrival at a note late, and a short note never quite at its
// height). The colour is judged on the median alone, which keeps a change of note as sharp as it
// was sung.
function smooth(fragment: Raw[]): Smoothed[] {
  const half = Math.floor(tuning.medianWidth / 2);
  const medians = fragment.map((_sample, index) =>
    median(fragment.slice(Math.max(0, index - half), index + half + 1).map((sample) => sample.midi)),
  );
  const forward = exponentialAverage(medians);
  const both = exponentialAverage(forward.reverse()).reverse();
  return fragment.map((sample, index) => ({
    intoMs: sample.intoMs,
    midi: both[index],
    judgedMidi: medians[index],
    judgedCents: 0, // set once the line is folded
  }));
}

function exponentialAverage(values: number[]): number[] {
  let average = values[0];
  return values.map((value, index) => {
    average = index === 0 ? average : average + tuning.smoothing * (value - average);
    return average;
  });
}

// ---- 6. Join the dot -------------------------------------------------------------------------

// The last fragment with the dot as its last point when the dot is joinable; the fragment itself
// otherwise. The line it is given is shared between renders, so it is copied, never changed.
function joinLead(
  line: Smoothed[],
  lead: SolfegeTraceLead | undefined,
  geometry: SolfegeStaffGeometry,
  register: number,
  params: SolfegeLevelParams,
): Smoothed[] {
  const last = line[line.length - 1];
  if (!lead || !last || !Number.isFinite(lead.intoMs) || !Number.isFinite(lead.midi)) {
    return line;
  }
  const behindMs = lead.intoMs - last.intoMs;
  if (behindMs <= 0 || behindMs > tuning.leadJoinMs || lead.intoMs >= geometry.lengthMs) {
    return line;
  }
  const midi = params.octaveCounts ? nearestOctave(lead.midi, last.midi) : lead.midi;
  const { cents } = solfegeFoldCents(centsFromSounding(geometry, lead.intoMs, midi), register, params);
  return [...line, { intoMs: lead.intoMs, midi, judgedMidi: midi, judgedCents: cents }];
}

// ---- 5. Colour, and 7. Draw ------------------------------------------------------------------

// A point's colour by its distance from the note sounding then, or undefined inside a note's
// settle, where the voice is still travelling to it
function judge(point: Smoothed, geometry: SolfegeStaffGeometry, params: SolfegeLevelParams): ResultColor | undefined {
  const { startMs, endMs } = soundingNote(geometry, point.intoMs).slot;
  if (point.intoMs >= startMs && point.intoMs < startMs + solfegeSettleMs(endMs - startMs, params)) {
    return undefined;
  }
  return solfegeCentsColor(point.judgedCents, params);
}

function draw(
  line: Smoothed[],
  geometry: SolfegeStaffGeometry,
  params: SolfegeLevelParams,
  fragment: number,
): SolfegeTracePath[] {
  // Placed on the staff, with any point that would stand on (or behind) the one before dropped
  const placed: Array<Smoothed & { x: number; y: number }> = [];
  for (const point of line) {
    const x = solfegeTimeX(geometry, point.intoMs);
    const before = placed[placed.length - 1];
    if (!before || x - before.x > 1e-3) {
      placed.push({ ...point, x, y: solfegePitchY(geometry, point.midi) });
    }
  }
  if (placed.length < 2) {
    return [];
  }
  const colors = settledColors(
    placed.map((point) => judge(point, geometry, params)),
    placed.map((point) => point.judgedCents),
    params,
  );
  const runs = mergeShortRuns(
    placed.map((point) => point.intoMs),
    colors,
  );
  const points: SolfegeTracePoint[] = placed.map((point, index) => ({
    intoMs: point.intoMs,
    midi: point.midi,
    x: point.x,
    y: point.y,
    color: colors[index],
  }));
  const segments = monotoneSegments(points);
  return runs.map((run, index) => {
    const end = index + 1 < runs.length ? runs[index + 1].from : points.length - 1;
    const runPoints = points.slice(run.from, end + 1).map((point) => ({ ...point, color: run.color }));
    const first = points[run.from];
    const curves = segments
      .slice(run.from, end)
      .map(
        (segment) =>
          `C${num(segment.c1x)} ${num(segment.c1y)} ${num(segment.c2x)} ${num(segment.c2y)} ${num(segment.x)} ${num(segment.y)}`,
      );
    return { color: run.color, d: `M${num(first.x)} ${num(first.y)}${curves.join('')}`, fragment, points: runPoints };
  });
}

// Every point's colour: a settling point takes the next judged colour in the fragment, or the one
// before when nothing after it is judged, or its own (folded) distance's when nothing is
function settledColors(
  judged: (ResultColor | undefined)[],
  cents: number[],
  params: SolfegeLevelParams,
): ResultColor[] {
  const colors: ResultColor[] = new Array<ResultColor>(judged.length);
  let next: ResultColor | undefined;
  for (let index = judged.length - 1; index >= 0; index -= 1) {
    next = judged[index] ?? next;
    colors[index] = next as ResultColor;
  }
  let previous: ResultColor | undefined;
  for (let index = 0; index < judged.length; index += 1) {
    previous = judged[index] ?? previous;
    colors[index] = colors[index] ?? previous ?? solfegeCentsColor(cents[index], params);
  }
  return colors;
}

interface Run {
  from: number; // the index of its first point
  color: ResultColor;
}

// The fragment's colour runs with none shorter than minColorRunMs (unless the fragment is one run):
// the shortest is merged into the neighbour it is most like - both, when they share a colour - and
// again until none is short. A run lasts from its first point to the next run's first point (the
// last run to its last point and one sample's spacing beyond). Writes the merged colours back.
function mergeShortRuns(times: number[], colors: ResultColor[]): Run[] {
  const runs: Run[] = [];
  colors.forEach((color, index) => {
    if (runs.length === 0 || runs[runs.length - 1].color !== color) {
      runs.push({ from: index, color });
    }
  });
  const endOf = (index: number) =>
    index + 1 < runs.length ? times[runs[index + 1].from] : times[times.length - 1] + tuning.sampleSpacingMs;
  const lengthOf = (index: number) => endOf(index) - times[runs[index].from];
  while (runs.length > 1) {
    let shortest = 0;
    for (let index = 1; index < runs.length; index += 1) {
      if (lengthOf(index) < lengthOf(shortest)) {
        shortest = index;
      }
    }
    if (lengthOf(shortest) >= tuning.minColorRunMs) {
      break;
    }
    const before = runs[shortest - 1];
    const after = runs[shortest + 1];
    // Into the longer neighbour; when the two share a colour, the three become one
    const into =
      !after || (before && (before.color === after.color || lengthOf(shortest - 1) >= lengthOf(shortest + 1)))
        ? shortest - 1
        : shortest + 1;
    if (into < shortest) {
      runs.splice(shortest, after && after.color === before.color ? 2 : 1);
    } else {
      runs[shortest + 1] = { from: runs[shortest].from, color: after.color };
      runs.splice(shortest, 1);
    }
  }
  runs.forEach((run, index) => {
    const end = index + 1 < runs.length ? runs[index + 1].from : colors.length;
    colors.fill(run.color, run.from, end);
  });
  return runs;
}

interface Segment {
  c1x: number;
  c1y: number;
  c2x: number;
  c2y: number;
  x: number; // where the segment ends
  y: number;
}

// The cubic Béziers of a monotone cubic through the points, one per gap between them: Fritsch-
// Carlson tangents, zero at a peak or trough and never more than twice the shallower neighbouring
// secant, so the curve never overshoots (the points' x strictly increase)
function monotoneSegments(points: SolfegeTracePoint[]): Segment[] {
  const count = points.length;
  const widths = points.slice(1).map((point, index) => point.x - points[index].x);
  const secants = points.slice(1).map((point, index) => (point.y - points[index].y) / widths[index]);
  const tangents = points.map((_point, index) => {
    if (index === 0 || index === count - 1) {
      return 0; // replaced below
    }
    const [s0, s1] = [secants[index - 1], secants[index]];
    if (s0 * s1 <= 0) {
      return 0;
    }
    const [h0, h1] = [widths[index - 1], widths[index]];
    const blended = (s0 * h1 + s1 * h0) / (h0 + h1);
    return Math.sign(s0) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(blended)) * 2;
  });
  if (count === 2) {
    tangents[0] = secants[0];
    tangents[1] = secants[0];
  } else {
    tangents[0] = endTangent(secants[0], tangents[1]);
    tangents[count - 1] = endTangent(secants[count - 2], tangents[count - 2]);
  }
  return widths.map((width, index) => {
    const from = points[index];
    const to = points[index + 1];
    const third = width / 3;
    return {
      c1x: from.x + third,
      c1y: from.y + tangents[index] * third,
      c2x: to.x - third,
      c2y: to.y - tangents[index + 1] * third,
      x: to.x,
      y: to.y,
    };
  });
}

// An end's tangent from its one secant and its neighbour's tangent, as d3's curveMonotoneX takes it,
// kept to the secant's sign so the end does not hook back
function endTangent(secant: number, neighbour: number): number {
  const tangent = (3 * secant - neighbour) / 2;
  if (Math.sign(tangent) !== Math.sign(secant)) {
    return 0;
  }
  return Math.abs(tangent) > 3 * Math.abs(secant) ? 3 * secant : tangent;
}

// Two decimals in the viewBox's units: finer than a phone's pixel, and short paths
function num(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded === 0 ? 0 : rounded); // never -0
}
